import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { initials, photoOf, tenureLabel } from "@/lib/orgUtils";
import { EmployeeDialog } from "@/components/EmployeeDialog";
import { Search, Plus, Pencil, UserX, Trash2, X, Building2 } from "lucide-react";

export default function DirectoryPage() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [views, setViews] = useState([]);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("");
  const [chartFilter, setChartFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [dialog, setDialog] = useState(null); // {emp?, chartId}
  const [chartPicker, setChartPicker] = useState(false);
  const [deptPrompt, setDeptPrompt] = useState(null);
  const canManage = user?.role !== "viewer";

  const load = useCallback(async () => {
    try {
      const [e, v] = await Promise.all([api.get("/employees"), api.get("/views")]);
      setEmployees(e.data);
      setViews(v.data);
      setSelectedIds(new Set());
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const chartName = useMemo(() => Object.fromEntries(views.map((v) => [v.id, v.name])), [views]);
  const editableCharts = views.filter((v) => v.can_edit);
  const depts = useMemo(() => [...new Set(employees.map((e) => e.department).filter(Boolean))].sort(), [employees]);
  const filtered = useMemo(() => employees.filter((e) =>
    (!dept || e.department === dept) &&
    (!chartFilter || e.chart_id === chartFilter) &&
    (!q || [e.full_name, e.designation, e.employee_id, e.email].some((f) => f?.toLowerCase().includes(q.toLowerCase())))
  ).sort((a, b) => a.full_name.localeCompare(b.full_name)), [employees, q, dept, chartFilter]);

  const allSelected = filtered.length > 0 && filtered.every((e) => selectedIds.has(e.id));
  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(filtered.map((e) => e.id)));
  };
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const bulk = async (action, value = null, confirmMsg = null) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      const { data } = await api.post("/employees/bulk", { ids: [...selectedIds], action, value });
      toast.success(`${data.affected} people ${action === "delete" ? "permanently deleted" : action === "exit" ? "marked exited" : "updated"}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="h-full overflow-auto p-8" data-testid="directory-page">
      <div className="max-w-6xl mx-auto pb-20">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-black tracking-tight">People Directory</h1>
            <p className="text-[#676a82] font-medium mt-1">
              Everyone across all charts — {employees.filter((e) => e.status === "active").length} active · {employees.filter((e) => e.status === "exited").length} exited
            </p>
          </div>
          {canManage && editableCharts.length > 0 && (
            <button data-testid="add-employee-btn" onClick={() => setChartPicker(true)} className="flex items-center gap-2 px-5 h-11 rounded-xl bg-[#6161FF] hover:bg-[#5050E5] text-white font-bold">
              <Plus size={16} /> Add person
            </button>
          )}
        </div>
        <div className="mt-6 flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-64">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9699AD]" />
            <input data-testid="directory-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people…"
              className="w-full h-10 pl-9 pr-3 rounded-xl bg-white border border-[#181B34]/10 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#6161FF]/25" />
          </div>
          <select data-testid="directory-chart-filter" value={chartFilter} onChange={(e) => setChartFilter(e.target.value)}
            className="h-10 px-3 rounded-xl bg-white border border-[#181B34]/10 text-sm font-bold max-w-52">
            <option value="">All charts</option>
            {views.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <select data-testid="directory-dept-filter" value={dept} onChange={(e) => setDept(e.target.value)}
            className="h-10 px-3 rounded-xl bg-white border border-[#181B34]/10 text-sm font-bold">
            <option value="">All departments</option>
            {depts.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>

        <div className="mt-5 bg-white rounded-2xl border border-[#181B34]/10 overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-bold uppercase tracking-[0.15em] text-[#9699AD] border-b border-[#181B34]/8">
                {canManage && (
                  <th className="pl-4 py-3 w-8">
                    <input type="checkbox" data-testid="select-all-checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 accent-[#6161FF] cursor-pointer" />
                  </th>
                )}
                <th className="px-4 py-3">Person</th>
                <th className="px-4 py-3">Designation</th>
                <th className="px-4 py-3">Chart</th>
                <th className="px-4 py-3">Department</th>
                <th className="px-4 py-3">Tenure</th>
                <th className="px-4 py-3">Status</th>
                {canManage && <th className="px-4 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} data-testid={`directory-row-${e.employee_id}`} className={`border-b border-[#181B34]/5 hover:bg-[#F6F7FB] ${selectedIds.has(e.id) ? "bg-[#6161FF]/5" : ""}`}>
                  {canManage && (
                    <td className="pl-4 py-2.5">
                      <input type="checkbox" data-testid={`select-row-${e.employee_id}`} checked={selectedIds.has(e.id)} onChange={() => toggleOne(e.id)} className="w-4 h-4 accent-[#6161FF] cursor-pointer" />
                    </td>
                  )}
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      {photoOf(e) ? <img src={photoOf(e)} alt="" className="w-8 h-8 rounded-full object-cover" /> :
                        <span className="w-8 h-8 rounded-full bg-[#F0F3FF] text-[#6161FF] flex items-center justify-center text-[10px] font-bold">{initials(e.full_name)}</span>}
                      <div>
                        <p className="font-bold">{e.full_name}</p>
                        <p className="text-[11px] font-mono text-[#9699AD]">{e.employee_id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 font-semibold text-[#4b4e63]">{e.designation}</td>
                  <td className="px-4 py-2.5">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-[#6161FF]/10 text-[#6161FF]">{chartName[e.chart_id] || "—"}</span>
                  </td>
                  <td className="px-4 py-2.5">{e.department && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-[#579BFC]/15 text-[#2b6cb8]">{e.department}</span>}</td>
                  <td className="px-4 py-2.5 font-semibold text-[#676a82]">{e.date_of_joining ? tenureLabel(e.date_of_joining) : "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${e.status === "active" ? "bg-[#00C875]/15 text-[#00925a]" : "bg-[#181B34]/8 text-[#676a82]"}`}>{e.status}</span>
                  </td>
                  {canManage && (
                    <td className="px-4 py-2.5 text-right">
                      <button data-testid={`edit-emp-${e.employee_id}`} onClick={() => setDialog({ emp: e, chartId: e.chart_id })} className="w-7 h-7 rounded-lg hover:bg-[#F0F3FF] text-[#676a82] inline-flex items-center justify-center"><Pencil size={13} /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="p-8 text-center text-[#676a82] font-medium">No people match your filters.</p>}
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 pl-4 pr-2 py-2 rounded-2xl bg-[#181B34] text-white shadow-2xl anim-in" data-testid="bulk-actions-bar">
          <span className="text-sm font-black">{selectedIds.size} selected</span>
          <span className="w-px h-5 bg-white/20 mx-1" />
          <button data-testid="bulk-exit-btn" onClick={() => bulk("exit", null, `Mark ${selectedIds.size} people as exited?`)}
            className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-bold"><UserX size={13} /> Mark exited</button>
          <button data-testid="bulk-dept-btn" onClick={() => setDeptPrompt("")}
            className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-bold"><Building2 size={13} /> Set department</button>
          <button data-testid="bulk-delete-btn" onClick={() => bulk("delete", null, `PERMANENTLY delete ${selectedIds.size} people? This cannot be undone.`)}
            className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-[#E2445C] hover:bg-[#c93850] text-xs font-bold"><Trash2 size={13} /> Delete forever</button>
          <button data-testid="bulk-clear-btn" onClick={() => setSelectedIds(new Set())} className="w-9 h-9 rounded-xl hover:bg-white/10 flex items-center justify-center"><X size={15} /></button>
        </div>
      )}

      {deptPrompt !== null && (
        <div className="fixed inset-0 z-50 bg-[#181B34]/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setDeptPrompt(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()} data-testid="bulk-dept-dialog">
            <h3 className="text-lg font-black">Set department for {selectedIds.size} people</h3>
            <input data-testid="bulk-dept-input" autoFocus value={deptPrompt} onChange={(e) => setDeptPrompt(e.target.value)} placeholder="Department name" list="dept-options"
              className="mt-4 w-full h-11 px-3 rounded-xl border border-[#181B34]/15 font-semibold focus:outline-none focus:border-[#6161FF]" />
            <datalist id="dept-options">{depts.map((d) => <option key={d} value={d} />)}</datalist>
            <button data-testid="bulk-dept-apply" disabled={!deptPrompt.trim()} onClick={() => { bulk("set_department", deptPrompt.trim()); setDeptPrompt(null); }}
              className="mt-4 w-full h-11 rounded-xl bg-[#6161FF] text-white font-bold disabled:opacity-40">Apply</button>
          </div>
        </div>
      )}

      {chartPicker && (
        <div className="fixed inset-0 z-50 bg-[#181B34]/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setChartPicker(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()} data-testid="chart-picker-dialog">
            <h3 className="text-lg font-black">Which chart is this person for?</h3>
            <div className="mt-4 space-y-2 max-h-64 overflow-auto">
              {editableCharts.map((v) => (
                <button key={v.id} data-testid={`pick-chart-${v.id}`} onClick={() => { setChartPicker(false); setDialog({ chartId: v.id }); }}
                  className="w-full text-left p-3 rounded-xl border border-[#181B34]/10 hover:border-[#6161FF] font-bold text-sm">
                  {v.name} <span className="text-xs font-semibold text-[#9699AD]">· {v.headcount} people</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {dialog && (
        <EmployeeDialog emp={dialog.emp || null} chartId={dialog.chartId}
          chartEmployees={employees.filter((e) => e.chart_id === dialog.chartId)}
          onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />
      )}
    </div>
  );
}
